import React, { useContext, useEffect, useState } from "react";

import { Edit } from "@mui/icons-material";
import {
  Alert,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Tooltip,
} from "@mui/material";
import { Link } from "react-router-dom";

import { SubscriptionSeatTypes } from "@safetyculture/s12-apis-connect-web/s12/common/seat_types_pb";
import { ActionType } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/action_pb";

import {
  getOrgInspectionQuota,
  updateOrgInspectionQuota,
} from "api/endpoints/organisation";
import { callOptionsHeaders } from "api/helpers";
import ConfirmDialog from "components/shared/confirm-dialog";
import { subscriptionSeatTypeDisplayMapping } from "constants/component";
import { MAX_ADDITIONAL_ORG_INSPECTION_QUOTA } from "constants/quota";
import { OrgInspectionQuotaContext } from "context/organisation-context";
import { PermissionsContext } from "context/permissions-context";
import { SnackbarContext } from "context/snackbar-context";
import {
  useOrgName,
  useOrgS12ID,
  useOrgUUID,
} from "helpers/hooks/use-org-details";

export interface OrgInspectionQuotaDisplayProps {
  regionCode: string;
}

const OrgInspectionQuotaDisplay = ({
  regionCode,
}: OrgInspectionQuotaDisplayProps) => {
  const orgUUID = useOrgUUID();
  const orgS12ID = useOrgS12ID();
  const orgName = useOrgName();
  const { hasPermissions, permissions } = useContext(PermissionsContext);
  const { orgInspectionQuota, setOrgInspectionQuota } = useContext(
    OrgInspectionQuotaContext,
  );
  const { showError } = useContext(SnackbarContext);
  const [open, setOpen] = useState(false);
  const [seatType, setSeatType] = useState<SubscriptionSeatTypes>(
    SubscriptionSeatTypes.SUBSCRIPTION_SEAT_TYPE_UNSPECIFIED,
  );
  const [seatTypeDisplay, setSeatTypeDisplay] = useState("");
  const [quota, setQuota] = useState(0);
  const [orgQuota, setOrgQuota] = useState(0);

  const handleUpdateOrgInspectionQuota = async () => {
    await updateOrgInspectionQuota(
      {
        orgId: orgUUID,
        seatType: seatType,
        quota: quota,
      },
      callOptionsHeaders(regionCode, orgS12ID),
    )
      .then(async () => {
        const newQuota = await getOrgInspectionQuota(
          {
            orgId: orgUUID,
          },
          callOptionsHeaders(regionCode, orgS12ID),
        );
        setOrgInspectionQuota(newQuota);
      })
      .catch(showError);
  };

  const getQuotaForSeatType = (seatType: SubscriptionSeatTypes) => {
    return (
      orgInspectionQuota?.inspectionQuota?.find((x) => x.seatType == seatType)
        ?.quota ?? 0
    );
  };

  useEffect(() => {
    const currentQuota = getQuotaForSeatType(seatType);
    setOrgQuota(currentQuota);
    setQuota(currentQuota);
  }, [orgInspectionQuota, seatType]);

  return (
    <>
      <b>Lite: </b>
      {orgInspectionQuota?.inspectionQuota?.find(
        (x) => x.seatType == SubscriptionSeatTypes.SUBSCRIPTION_SEAT_TYPE_LITE,
      )?.quota ?? 0}{" "}
      | <b>Free: </b>
      {orgInspectionQuota?.inspectionQuota?.find(
        (x) =>
          x.seatType ==
          SubscriptionSeatTypes.SUBSCRIPTION_SEAT_TYPE_COLLABORATOR,
      )?.quota ?? 0}
      <Tooltip title="Modify additional org inspection quota">
        <IconButton
          color="primary"
          data-testid="open-change-org-inspection-quota-modal-btn"
          disabled={
            permissions.isPermissionsLoading ||
            !hasPermissions([ActionType.UPDATE_ORG_INSPECTION_QUOTA])
          }
          style={{
            position: "relative",
            bottom: 2,
            left: 5,
            padding: 1,
          }}
          onClick={() => {
            setOpen(true);
          }}
        >
          <Edit fontSize="small" />
        </IconButton>
      </Tooltip>
      <ConfirmDialog
        title="Modify Additional Org Inspection Quota"
        open={open}
        cancelBtnLabel="Cancel"
        confirmBtnLabel="Update"
        setOpen={setOpen}
        onConfirm={() => {
          handleUpdateOrgInspectionQuota();
          setQuota(0);
        }}
        disabled={
          ![
            SubscriptionSeatTypes.SUBSCRIPTION_SEAT_TYPE_LITE,
            SubscriptionSeatTypes.SUBSCRIPTION_SEAT_TYPE_COLLABORATOR,
          ].includes(seatType) || orgQuota == quota
        }
        onClose={() => {
          setSeatTypeDisplay(
            subscriptionSeatTypeDisplayMapping[
              SubscriptionSeatTypes.SUBSCRIPTION_SEAT_TYPE_UNSPECIFIED
            ],
          );
          setSeatType(SubscriptionSeatTypes.SUBSCRIPTION_SEAT_TYPE_UNSPECIFIED);
          setQuota(0);
        }}
        onCancel={() => {
          setSeatTypeDisplay(
            subscriptionSeatTypeDisplayMapping[
              SubscriptionSeatTypes.SUBSCRIPTION_SEAT_TYPE_UNSPECIFIED
            ],
          );
          setSeatType(SubscriptionSeatTypes.SUBSCRIPTION_SEAT_TYPE_UNSPECIFIED);
          setQuota(0);
        }}
      >
        <Alert severity="info" variant="filled" style={{ margin: "2px" }}>
          You are about to modify additional org inspection quota for {orgName}
          &apos;s <b>{subscriptionSeatTypeDisplayMapping[seatType]}</b>{" "}
          <b>
            from {getQuotaForSeatType(seatType)} to {quota}
          </b>
          .<br />
          The maximum additional org inspection quota is{" "}
          {MAX_ADDITIONAL_ORG_INSPECTION_QUOTA}. <br />A user&apos;s inspection
          quota is calculated by the sum of:
          <ul>
            <li>the seat type&apos;s default inspection quota limit</li>
            <li>additional user inspection quota</li>
            <li>additional org inspection quota</li>
          </ul>{" "}
          <b>
            quota = default quota + user additional quota + org additional quota
          </b>
          <br />
          <p />
          <Link
            to={{
              pathname: "https://safetyculture.atlassian.net/wiki/x/pICA0w",
            }}
            target="_blank"
            rel="noopener noreferrer"
            className="remove-link-style"
          >
            LEARN MORE
          </Link>
        </Alert>
        <FormControl fullWidth sx={{ m: 1 }}>
          <InputLabel id="seat-type-select-select-label">Seat Type</InputLabel>
          <Select
            data-testid={"seat-type-select"}
            id="seat-type-select"
            label="Seat Type"
            value={seatTypeDisplay}
            onChange={(event: SelectChangeEvent) => {
              const value = event.target.value as string;
              setSeatType(Number(event.target.value) as SubscriptionSeatTypes);
              setSeatTypeDisplay(value);
            }}
          >
            <MenuItem value={SubscriptionSeatTypes.SUBSCRIPTION_SEAT_TYPE_LITE}>
              {
                subscriptionSeatTypeDisplayMapping[
                  SubscriptionSeatTypes.SUBSCRIPTION_SEAT_TYPE_LITE
                ]
              }
            </MenuItem>
            <MenuItem
              value={SubscriptionSeatTypes.SUBSCRIPTION_SEAT_TYPE_COLLABORATOR}
            >
              {
                subscriptionSeatTypeDisplayMapping[
                  SubscriptionSeatTypes.SUBSCRIPTION_SEAT_TYPE_COLLABORATOR
                ]
              }
            </MenuItem>
          </Select>
        </FormControl>
        <TextField
          id="outlined-basic"
          label="Additional Org Inspection Quota"
          variant="outlined"
          type="number"
          fullWidth
          sx={{ m: 1 }}
          value={quota}
          onChange={(e) => {
            setQuota(parseInt(e.target.value));
          }}
          inputProps={{
            "data-anchor": "do-numOrgInspections-input",
            "data-testid": "do-numOrgInspections-input",
            min: 0,
            max: 250,
          }}
          data-testid={"quota-textfield"}
        />
      </ConfirmDialog>
    </>
  );
};

export default OrgInspectionQuotaDisplay;
